import { DataTable, Figure, Finding, Tile, Panel, column } from "./trade_review_parts"
import { fmt_int, fmt_pct0, fmt_pct1, fmt_signed_pct, fmt_usd2, fmt_usd_short, scorecard_row, trade_review } from "./trade_review_model"

const CALL_TONE: Record<string, string> = {
  Protect: "text-emerald-700",
  Keep: "text-emerald-700",
  Grow: "text-emerald-700",
  "Go deeper, fewer": "text-emerald-700",
  Watch: "text-amber-700",
  "Get detail": "text-amber-700",
  Rework: "text-red-700",
  "Test pausing": "text-red-700",
}

const SCORECARD_COLUMNS: column<scorecard_row>[] = [
  { header: "Retailer", left: true, cell: (row: scorecard_row) => row.retailer },
  { header: "Sales", cell: (row: scorecard_row) => fmt_usd_short(row.sales) },
  { header: "Trade rate", cell: (row: scorecard_row) => fmt_pct1(row.trade_pct) },
  { header: "Promos", cell: (row: scorecard_row) => fmt_int(row.events) },
  { header: "Lift", cell: (row: scorecard_row) => fmt_signed_pct(row.lift) },
  { header: "Model check", cell: (row: scorecard_row) => `${fmt_signed_pct(row.model)} (${fmt_signed_pct(row.model_lo)} to ${fmt_signed_pct(row.model_hi)})` },
  { header: "Trade per $1 extra sales", cell: (row: scorecard_row) => (row.per == null ? "No clear lift" : fmt_usd2(row.per)) },
  { header: "Fill rate on promo", cell: (row: scorecard_row) => fmt_pct0(row.fill) },
  { header: "Call", left: true, cell: (row: scorecard_row) => <span class={`font-medium whitespace-nowrap ${CALL_TONE[row.read] ?? ""}`}>{row.read}</span> },
  { header: "Why", left: true, wrap: true, cell: (row: scorecard_row) => row.why },
]

export function OverviewTab({ data }: { data: trade_review }) {
  const s = data.summary
  return (
    <div class="space-y-6">
      <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile
          label="Promo lift, shipped units"
          value={fmt_signed_pct(s["lift"] ?? 0)}
          note={<><Figure summary={s} field="events_n" as="int" /> promos measured against their own baseline</>}
        />
        <Tile
          label="Trade spent per $1 of extra sales"
          value={fmt_usd2(s["per"] ?? 0)}
          note={<><Figure summary={s} field="base_share" as="pct0" /> of scanned units would have sold anyway</>}
        />
        <Tile
          label="Promo dollars explained"
          value={fmt_pct0(s["explained_pct"] ?? 0)}
          note={<>Tied to a retailer, item, dates and rate. <Figure summary={s} field="matched_pct" as="pct0" /> match a calendar row today</>}
        />
        <Tile
          label="Trade and KeHE costs, share of sales"
          value={fmt_pct1(s["all_in_pct"] ?? 0)}
          note={<>Only <Figure summary={s} field="working_share" as="pct0" /> reaches the shopper, on <Figure summary={s} field="sales" as="usd_short" /> sold through KeHE</>}
        />
      </div>

      <Panel
        title="Where the trade dollars work"
        note="Largest retailers first. Lift compares promo weeks with nearby non-promo weeks. Model check is the same lift with retailer, item and week controls, with its 95% range. The call in the last columns is our recommendation.">
        <DataTable columns={SCORECARD_COLUMNS} rows={data.retailer_scorecard} row_key={(row: scorecard_row) => row.retailer} />
      </Panel>

      <div class="grid gap-4 md:grid-cols-2">
        <Finding title="Most of the cost never reaches the shopper">
          Working trade was <Figure summary={s} field="working" as="usd_short" />. Non-working costs were <Figure summary={s} field="nonworking" as="usd_short" />, or{" "}
          <Figure summary={s} field="nw_per_working" as="usd2" /> for every $1 that works. KeHE's own terms make up <Figure summary={s} field="kehe_terms" as="usd_short" /> of that.
        </Finding>
        <Finding title="Depth buys lift at about the same cost">
          At Sprouts, Triple Crème lifted <Figure summary={s} field="sprouts_tc_2" as="signed_pct" /> at $2 off and <Figure summary={s} field="sprouts_tc_1" as="signed_pct" /> at $1 off. Cost per extra sales dollar stays close to flat as depth grows, so going deeper costs about the same for each dollar it brings in.
        </Finding>
        <Finding title="The calendar stops in June">
          KeHE billed <Figure summary={s} field="y26_no_row" as="usd_short" /> for 2026 promos with no calendar row, and most of that came after June. We rebuilt <Figure summary={s} field="rebuilt_n" as="int" /> of those promos from the billbacks themselves.
        </Finding>
        <Finding title="Accruals run well above what gets billed">
          Across <Figure summary={s} field="plan_n" as="int" /> matched TPRs, the plan was <Figure summary={s} field="plan_amt" as="usd_short" /> and billing was <Figure summary={s} field="billed_amt" as="usd_short" />. <Figure summary={s} field="under_n" as="int" /> came in under 75% of plan and <Figure summary={s} field="over_n" as="int" /> ran over 125%.
        </Finding>
        <Finding title={<>About <Figure summary={s} field="watch_quiet_wk" as="usd_short" /> a week of KeHE volume has gone quiet</>}>
          Sprouts stopped taking Petites and Marinated after June. Laura Chenel goat fell away at Safeway Seattle in August. None of this shows up in a promo report. Kroger's <Figure summary={s} field="kroger_wk" as="usd_short" /> a week is not in that figure: Ralphs and Fry's moved from KeHE to GFI in June.
        </Finding>
        <Finding title="Promos are running on items KeHE can't fill">
          Marinated filled at <Figure summary={s} field="marinated_fill" as="pct0" /> this year while it was on promo at Sprouts. Across everything, KeHE shows <Figure summary={s} field="short_total" as="usd_short" /> ordered and not shipped. Some of that is reordered later, so treat it as a ceiling.
        </Finding>
        <Finding title="A new KeHE fine is growing fast">
          <Figure summary={s} field="nedi_n" as="int" /> "810 non-compliant invoice" charges totaling <Figure summary={s} field="nedi_amt" as="usd_short" /> have landed since July, more each month. This is an invoicing setup issue, so it should be fixable.
        </Finding>
        <Finding title="Off-invoice promos are a Costco matter">
          We checked <Figure summary={s} field="ia_docs" as="int" /> KeHE adjustment forms and found no off-invoice promos, which fits your team's note that Costco is the only customer set up that way. KeHE's own 2% allowances are off-invoice by contract and are already counted.
        </Finding>
      </div>
    </div>
  )
}
